/**
 * Batch Filters Panel Component
 * Comprehensive filtering options for available batches
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import type { BatchFilters } from '@/types/availableBatches';
import { EXPIRY_RANGE_OPTIONS } from '@/types/availableBatches';
import { ProductType, QualityGrade, ProductTypeConfig, QualityGradeConfig } from '@/types/inventory';
import {
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Package,
  Award,
  Shield,
  Calendar,
  MapPin,
  AlertTriangle,
  Droplet,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface BatchFiltersPanelProps {
  filters: BatchFilters;
  onFiltersChange: (filters: BatchFilters) => void;
  onReset: () => void;
  activeFilterCount: number;
  className?: string;
}

export function BatchFiltersPanel({
  filters,
  onFiltersChange,
  onReset,
  activeFilterCount,
  className
}: BatchFiltersPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleProductTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      productType: value as ProductType | 'ALL'
    });
  };

  const handleQualityGradeToggle = (grade: QualityGrade) => {
    const newGrades = filters.qualityGrades.includes(grade)
      ? filters.qualityGrades.filter(g => g !== grade)
      : [...filters.qualityGrades, grade];

    onFiltersChange({
      ...filters,
      qualityGrades: newGrades
    });
  };

  const handlePesoToggle = (value: boolean) => {
    onFiltersChange({
      ...filters,
      pesoCompliant: value ? true : null
    });
  };

  const handleExpiryRangeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      expiryRange: value as BatchFilters['expiryRange']
    });
  };

  const handleStorageLocationChange = (value: string) => {
    onFiltersChange({
      ...filters,
      storageLocation: value
    });
  };

  const handleLowStockToggle = (checked: boolean) => {
    onFiltersChange({
      ...filters,
      lowStockOnly: checked
    });
  };

  const handleMinPurityChange = (value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    onFiltersChange({
      ...filters,
      minPurity: numValue
    });
  };

  const handleMaxPurityChange = (value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    onFiltersChange({
      ...filters,
      maxPurity: numValue
    });
  };

  const handleMinQuantityChange = (value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    onFiltersChange({
      ...filters,
      minQuantity: numValue
    });
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <CardTitle className="text-lg">Filters</CardTitle>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {activeFilterCount} active
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                className="text-gray-600 hover:text-gray-900"
              >
                <X className="mr-1 h-4 w-4" />
                Clear All
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-6">
          {/* Cluster ID Filter - Biogas Production Location */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-4 w-4" />
              Production Plant (Cluster)
            </Label>
            <Input
              type="text"
              placeholder="e.g., Plant 115, Cluster ID..."
              value={filters.storageLocation || ''}
              onChange={(e) => {
                const clusterValue = e.target.value;
                onFiltersChange({
                  ...filters,
                  clusterId: clusterValue,
                  storageLocation: clusterValue
                });
              }}
            />
            {filters.storageLocation && (
              <p className="text-xs text-gray-600">
                Filtering batches from cluster: {filters.storageLocation}
              </p>
            )}
          </div>

          {/* Quality Grade Filter (Multiple Selection) */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Award className="h-4 w-4" />
              Quality Grade
            </Label>
            <div className="space-y-2">
              {Object.entries(QualityGradeConfig).map(([key, config]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`grade-${key}`}
                    checked={filters.qualityGrades.includes(key as QualityGrade)}
                    onCheckedChange={() => handleQualityGradeToggle(key as QualityGrade)}
                  />
                  <label
                    htmlFor={`grade-${key}`}
                    className="flex-1 text-sm cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Grade {key}</span>
                      <span className="text-xs text-gray-500">{config.description}</span>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>


          {/* Expiry Range Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Calendar className="h-4 w-4" />
              Expiry Range
            </Label>
            <Select value={filters.expiryRange} onValueChange={handleExpiryRangeChange}>
              <SelectTrigger>
                <SelectValue placeholder="All batches" />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_RANGE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>


          {/* Methane Content (Purity) Filter */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Droplet className="h-4 w-4" />
              Methane Content (%)
            </Label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600">Min %</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={filters.minPurity !== undefined ? filters.minPurity : ''}
                    onChange={(e) => handleMinPurityChange(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Max %</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="100"
                    value={filters.maxPurity !== undefined ? filters.maxPurity : ''}
                    onChange={(e) => handleMaxPurityChange(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              {filters.minPurity !== undefined && filters.maxPurity !== undefined && (
                <p className="text-xs text-gray-600">
                  Methane content: {filters.minPurity}% - {filters.maxPurity}%
                </p>
              )}
            </div>
          </div>

          {/* Biogas Volume (Quantity) Filter */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Zap className="h-4 w-4" />
              Min Biogas Volume (m³)
            </Label>
            <Input
              type="number"
              min="0"
              step="0.1"
              placeholder="Enter minimum volume..."
              value={filters.minQuantity !== undefined ? filters.minQuantity : ''}
              onChange={(e) => handleMinQuantityChange(e.target.value)}
            />
            {filters.minQuantity !== undefined && filters.minQuantity > 0 && (
              <p className="text-xs text-gray-600">
                Showing batches with at least {filters.minQuantity} m³
              </p>
            )}
          </div>

          {/* Active Filters Summary */}
          {activeFilterCount > 0 && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 font-medium">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
                </span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={onReset}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Reset All
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
